"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleAlert as AlertCircle, ArrowRight, Bandage, Bug, CircleCheck as CheckCircle2, ChevronRight, Eye, Heart, CircleQuestionMark as HelpCircle, Pill, Scale, Shield, ShieldAlert, Sparkles, Stethoscope, Syringe, Thermometer, Trees, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const healthTopics = [{
  id: "allergies",
  title: "Allergies",
  icon: Sparkles,
  symptoms: ["Paw licking", "Skin redness", "Chronic itching", "Sneezing"],
  summary: "Identify environmental, food, and contact allergens affecting skin barriers and digestion.",
  to: "/PetHealthPage"
}, {
  id: "skin-coat",
  title: "Skin & Coat",
  icon: Bandage,
  symptoms: ["Hot spots", "Dull coat", "Dander flakes", "Hair loss"],
  summary: "Dermatological protocols for follicular infections, dermatitis, and seasonal shedding.",
  to: "/PetHealthPage"
}, {
  id: "digestive-health",
  title: "Digestive Health",
  icon: Activity,
  symptoms: ["Acute vomiting", "Loose stools", "Inappetence", "Gas"],
  summary: "Gastrointestinal balance, gut microbiome restoration, and irritable bowel management.",
  to: "/PetHealthPage"
}, {
  id: "dental-health",
  title: "Dental Health",
  icon: CheckCircle2,
  symptoms: ["Halitosis", "Tartar build-up", "Bleeding gums", "Chewing pain"],
  summary: "Periodontal prevention, ultrasonic scaling milestones, and home oral hygiene routines.",
  to: "/PetHealthPage"
}, {
  id: "ear-health",
  title: "Ear Health",
  icon: HelpCircle,
  symptoms: ["Head shaking", "Dark discharge", "Odor", "Ear scratching"],
  summary: "Otitis externa care, canal cleaning techniques, and chronic yeast prevention.",
  to: "/PetHealthPage"
}, {
  id: "eye-health",
  title: "Eye Health",
  icon: Eye,
  symptoms: ["Cloudiness", "Watery discharge", "Squinting", "Red sclera"],
  summary: "Corneal health, tear duct obstructions, cataracts, and pressure evaluations.",
  to: "/PetHealthPage"
}, {
  id: "heart-health",
  title: "Heart Health",
  icon: Heart,
  symptoms: ["Persistent coughing", "Exercise intolerance", "Rapid breathing"],
  summary: "Cardiovascular screening, heart murmur stages, and blood pressure monitoring.",
  to: "/PetHealthPage"
}, {
  id: "joint-mobility",
  title: "Joint & Mobility",
  icon: Activity,
  symptoms: ["Stiff gait", "Slow rising", "Limping", "Reluctance to jump"],
  summary: "Osteoarthritis management, cartilage preservation, and physical rehabilitation.",
  to: "/PetHealthPage"
}, {
  id: "parasites",
  title: "Parasites",
  icon: Bug,
  symptoms: ["Potbelly", "Scooting", "Weight drop", "Dull demeanor"],
  summary: "Internal parasite diagnosis covering roundworms, tapeworms, and Giardia.",
  to: "/PetHealthPage"
}, {
  id: "fleas-ticks",
  title: "Fleas & Ticks",
  icon: Trees,
  symptoms: ["Flea dirt", "Severe biting", "Tick attachment", "Fever"],
  summary: "Vector-borne pathogen protection, topical preventatives, and oral parasiticide schedules.",
  to: "/PetHealthPage"
}, {
  id: "weight-management",
  title: "Weight Management",
  icon: Scale,
  symptoms: ["Loss of waistline", "Lethargy", "Panting on walks"],
  summary: "Body Condition Scoring (BCS), caloric titration, and metabolic health plans.",
  to: "/PetHealthPage"
}, {
  id: "kidney-urinary",
  title: "Kidney & Urinary Health",
  icon: Activity,
  symptoms: ["Frequent urination", "Straining", "Increased thirst", "Accidents"],
  summary: "Renal staging, FLUTD assessment, crystal formation, and hydration protocols.",
  to: "/PetHealthPage"
}, {
  id: "diabetes",
  title: "Diabetes",
  icon: Zap,
  symptoms: ["Excessive thirst", "Frequent urination", "Voracious hunger", "Weight drop"],
  summary: "Glucose curves, insulin administration routines, and low-glycemic dietary planning.",
  to: "/PetHealthPage"
}, {
  id: "cancer",
  title: "Cancer & Oncology",
  icon: AlertCircle,
  symptoms: ["Non-healing sores", "Unexplained lumps", "Swollen nodes", "Weight loss"],
  summary: "Early oncology detection markers, biopsy evaluations, and palliative care.",
  to: "/PetHealthPage"
}, {
  id: "infections",
  title: "Infections",
  icon: Thermometer,
  symptoms: ["Elevated temperature", "Lethargy", "Pus discharge", "Loss of appetite"],
  summary: "Bacterial, viral, and fungal containment strategies alongside antibiotic stewardship.",
  to: "/PetHealthPage"
}, {
  id: "poisoning-toxicity",
  title: "Poisoning & Toxicity",
  icon: ShieldAlert,
  symptoms: ["Sudden collapse", "Seizures", "Excessive salivation", "Vomiting blood"],
  summary: "Critical toxin response index covering plants, human medications, and household hazards.",
  to: "/PetHealthPage"
}, {
  id: "vaccinations",
  title: "Vaccinations",
  icon: Syringe,
  symptoms: ["Mild post-shot fatigue", "Localized swelling"],
  summary: "Core vs. non-core schedules, antibody titer testing, and maternal immunity transition.",
  to: "/PetHealthPage"
}, {
  id: "preventive-care",
  title: "Preventive Care",
  icon: Shield,
  symptoms: ["Routine wellness", "Baseline bloodwork", "Annual checkup"],
  summary: "Life-stage diagnostic baselines, early disease interception, and longevity planning.",
  to: "/PetHealthPage"
}, {
  id: "emergency-symptoms",
  title: "Emergency Symptoms",
  icon: AlertCircle,
  symptoms: ["Pale gums", "Distended abdomen", "Unresponsiveness", "Severe trauma"],
  summary: "Immediate triage triggers that require direct veterinary ER intervention.",
  to: "/PetHealthPage"
}, {
  id: "medications",
  title: "Medications",
  icon: Pill,
  symptoms: ["Dosage questions", "Side effect tracking", "Drug interactions"],
  summary: "Safe pharmaceutical administration, compound formulations, and safety disclosures.",
  to: "/PetHealthPage"
}, {
  id: "supplements",
  title: "Supplements",
  icon: Sparkles,
  symptoms: ["Nutrient gaps", "Senior stiffness", "Coat luster needs"],
  summary: "Evidence-evaluated omega oils, joint chondroprotectants, and certified probiotics.",
  to: "/PetHealthPage"
}, {
  id: "senior-pet-health",
  title: "Senior Pet Health",
  icon: Stethoscope,
  symptoms: ["Cognitive confusion", "Night pacing", "Vision decline", "Muscle loss"],
  summary: "Geriatric comfort protocols, organ function screening, and environmental adaptations.",
  to: "/PetHealthPage"
}];
export default function Topics() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.04
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="health-topics" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-border text-foreground text-xs font-semibold tracking-wider uppercase mb-4">
            <span className="size-2 rounded-full bg-chart-1" />
            Diagnostic Reference Index
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Explore pet health topics
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Browse evidence-informed guidance covering common symptoms, preventive routines, and clinical steps across 22 primary veterinary domains.
          </p>
        </div>

        {/* Clinical Governance Banner */}
        <div className="mb-12 rounded-xl bg-card border border-border border-l-4 border-l-chart-1 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs font-semibold">
                Clinical Governance Standard
              </Badge>
              <span className="text-xs text-muted-foreground">Updated for 2025</span>
            </div>
            <h3 className="font-serif text-lg md:text-xl font-semibold text-foreground">
              Every topic is reviewed by licensed veterinary professionals
            </h3>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Our guides cite peer-reviewed veterinary literature, clinical trials, and pharmacology benchmarks to help you care for your pet with confidence.
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0">
            <Link to="/AboutPetsWhirlPage">Review editorial process</Link>
          </Button>
        </div>

        {/* 22 Topics Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.05
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {healthTopics.map((topic, i) => {
          const Icon = topic.icon;
          return <motion.div key={topic.id} variants={itemVariants} data-index={i} className="h-full">
                <Card className="h-full flex flex-col justify-between bg-card hover:bg-background border-border hover:border-chart-1/40 hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-300 rounded-xl overflow-hidden group">
                  <CardHeader className="pb-3 pt-6 px-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="size-10 rounded-lg bg-secondary text-foreground flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <Icon className="size-5 stroke-[1.75]" />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-xl text-foreground font-semibold group-hover:text-primary transition-colors">
                      {topic.title}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground leading-relaxed pt-1">
                      {topic.summary}
                    </p>
                  </CardHeader>

                  <CardContent className="px-6 pb-6 pt-0 flex-1 flex flex-col justify-between">
                    <div className="pt-2 border-t border-border/70 mb-4">
                      <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Common signs:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {topic.symptoms.map((symptom, symptomIndex) => <span key={symptomIndex} className="text-[11px] px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground border border-border/50">
                            {symptom}
                          </span>)}
                      </div>
                    </div>

                    <div className="pt-2">
                      <Link to={topic.to} className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground group-hover:text-chart-1 transition-colors">
                        <span>Open diagnostic guide</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Emergency Banner */}
        <div className="mt-14 p-6 rounded-xl bg-destructive/5 border border-destructive/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="size-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
              <ShieldAlert className="size-5" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground">
                Experiencing an acute medical crisis?
              </h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                If your pet has collapsed, ingested known toxins, or shows pale gums, seek immediate emergency veterinary hospital care.
              </p>
            </div>
          </div>
          <Button asChild variant="destructive" size="sm" className="shrink-0">
            <Link to="/PetHealthPage">
              Emergency checklist <ChevronRight className="size-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}